<script setup lang="ts">
    /**
     * Content som ska renderas i footern i ReceptforskrivningForm.vue
     */

    import PlusIcon from "@/assets/plus_circle_white.svg";
    import SendIcon from "@/assets/send_white.svg";
    import SignFrame from "@/components/Auth/SignFrame.vue";
    import KButton from "@/components/Button.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { Listener } from "@/components/forms/Form/types";
    import { Option } from "@/components/forms/inputs/Autocomplete/types";
    import { useStore } from "@/store";
    import { StoredReceptforskrivning } from "@/store/modules/recept/receptforskrivningModule";
    import ApotekAutocomplete from "@/tabs/Receptforskrivning/ApotekAutocomplete.vue";
    import TabFooter from "@/tabs/TabFooter.vue";
    import { openDialog } from "@/utils";
    import { hasPersonnummer } from "@/utils/patient/patientData";
    import { Portal } from "portal-vue";
    import { computed, ref } from "vue";

    const store = useStore();

    const isSigning = ref(false);
    const formRef = ref();

    const profil = computed(() => store.getters["userData/currentProfile"]);
    const patient = computed(() => store.getters["patientData/patient"]);
    /**
     * Används för att hämta ut valt apotek. Det spelar ingen roll exakt vilken förskrivning det hämtas ifrån,
     * eftersom alla förskrivningar i en samling måste ha samma apotek valt
     */
    const receptforskrivningar = computed<StoredReceptforskrivning[]>(
        () => store.getters["receptforskrivning/receptforskrivningar"]
    );

    defineProps<{
        /**
         * Används för att få formulärkomponenterna att registrera sig med formuläret efter rendering
         * och för att targetta rätt tab med portalen. Behövs för att
         * formulärkomponenterna här renderas utanför KForm. Se ReceptforskrivningForm.vue hur det görs och används.
         * Du behöver troligen inte overrida det som sätts där.
         */
        tabId: number;
        loading: boolean;
    }>();

    const emit = defineEmits<{
        (e: "accept"): void;
        (e: "cancel"): void;
        (e: "action", payload: { step: "form" | "display" | "list"; id?: string }): void;
    }>();

    const onAccept = () => {
        isSigning.value = false;

        emit("accept");
    };

    const onCancel = () => {
        emit("cancel");
    };

    const onAdd = async () => {
        const result = await formRef?.value?.manualSubmit();

        if (!result) {
            return;
        }

        onAction({ step: "form" });
    };

    const onAction = ({ step, id }: { step: "form" | "display" | "list"; id?: string }) => {
        emit("action", { step, id });
    };

    const onSignError = () => {
        toggleSigning();

        openDialog("Misslyckades med att signera receptförskrivningen.", "error");
    };

    const toggleSigning = () => {
        isSigning.value = !isSigning.value;
    };

    const onSubmit = (formData: unknown) => {
        const typedFormData = formData as { apotek: Option & { adress: string } };

        if (typedFormData) {
            setApotekOnSamling({ value: typedFormData.apotek });
        }

        toggleSigning();
    };

    const setFormData = () => {
        return {
            apotek: receptforskrivningar?.value?.[0]?.apotek,
        };
    };

    /**
     * Sätter nyvalda apoteket på alla förskrivningar i samlingen
     */
    const setApotekOnSamling = ({ value }: { value: (Option & { adress: string }) | null }) => {
        if (value?.value !== receptforskrivningar?.value?.[0]?.apotek?.value) {
            receptforskrivningar?.value?.forEach?.((receptforskrivning) => {
                store.commit("receptforskrivning/partialUpdateReceptforskrivning", {
                    id: receptforskrivning.id,
                    receptforskrivning: { apotek: value },
                });
            });
        }
    };

    const formListeners: Listener[] = [
        {
            fieldNames: ["apotek"],
            runAtInitial: false,
            action: ({ values }) => {
                setApotekOnSamling({ value: (values as { apotek: (Option & { adress: string }) | null }).apotek });
            },
        },
    ];
</script>

<template>
    <portal :to="`tab-${tabId}`">
        <TabFooter class="receptforskrivning-samling-portal">
            <KForm
                ref="formRef"
                id="receptforskrivning-samling-portal"
                actionsVariant="slim"
                :initialValuesGetter="setFormData"
                :listeners="formListeners"
                @submit="onSubmit"
            >
                <ApotekAutocomplete
                    v-if="!hasPersonnummer({ personIdType: patient?.personIdType })"
                    class="span-two"
                    label="Skicka till apotek"
                    validation="required"
                />
                <template #actions>
                    <KButton type="submit" size="slim" variant="standard" :is-loading="loading">
                        <img :src="SendIcon" alt="Skicka samling" />
                        <span>Skicka samling</span>
                    </KButton>
                    <KButton type="button" size="small" variant="standard" @click="onAdd" :disabled="loading">
                        <img :src="PlusIcon" alt="Lägg till förskrivning" />
                        <span>Lägg till förskrivning</span>
                    </KButton>
                    <KButton type="button" size="small" variant="contour" @click="onCancel" :disabled="loading">
                        Avbryt
                    </KButton>
                </template>
            </KForm>
        </TabFooter>
        <SignFrame
            v-if="isSigning"
            @success="onAccept"
            @cancel="toggleSigning"
            @error="onSignError"
            title="Receptförskrivning"
            url="/nll/forskrivningar/"
            :data="{ patient: patient.id, accountableHealthcareProfessional: profil?.id }"
        />
    </portal>
</template>

<style scoped lang="scss">
    @import "@/style/form.scss";

    .receptforskrivning-samling-portal {
        .actions {
            display: grid;
            grid-auto-flow: column;
            grid-auto-columns: max-content;
            align-items: flex-end;
            min-width: 100%;
            column-gap: calc($form-column-gap / 2);
        }
    }
</style>
